import { useState } from 'react'
import StatusBadge from './StatusBadge.jsx'
import FileExplorer from './FileExplorer.jsx'
import DiffViewer from './DiffViewer.jsx'
import { listSnapshotFiles, getSnapshotDiff } from '../api/vestige.js'

function formatTime(dateStr) {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function JsonViewer({ data, label }) {
  const [open, setOpen] = useState(false)
  const isEmpty = !data || Object.keys(data).length === 0

  return (
    <div className="border border-border rounded overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 bg-surface2 hover:bg-surface text-xs text-text-dim hover:text-text transition-colors"
        aria-expanded={open}
        disabled={isEmpty}
      >
        <span className="font-mono tracking-wide">{label}</span>
        <span className="flex items-center gap-2">
          <span className="text-text-faint">{isEmpty ? 'empty' : `${Object.keys(data).length} keys`}</span>
          {!isEmpty && (
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
              className={`transition-transform ${open ? 'rotate-180' : ''}`}
            >
              <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          )}
        </span>
      </button>
      {open && !isEmpty && (
        <pre className="text-xs font-mono text-text-dim bg-ground px-3 py-3 max-h-48 overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-words leading-relaxed">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  )
}

function LazySection({ label, loader, children }) {
  const [open, setOpen] = useState(false)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)

  const toggle = async () => {
    if (!open && data === null) {
      setLoading(true)
      try {
        const result = await loader()
        setData(result)
      } catch {
        setData([])
      } finally {
        setLoading(false)
      }
    }
    setOpen((v) => !v)
  }

  return (
    <div className="border border-border rounded overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-3 py-2 bg-surface2 hover:bg-surface text-xs text-text-dim hover:text-text transition-colors"
        aria-expanded={open}
      >
        <span className="font-mono tracking-wide">{label}</span>
        <span className="flex items-center gap-2">
          {loading && <span className="text-text-faint text-2xs">loading…</span>}
          {!loading && data !== null && (
            <span className="text-text-faint text-2xs">{Array.isArray(data) ? `${data.length} files` : ''}</span>
          )}
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"
            className={`transition-transform ${open ? 'rotate-180' : ''}`}>
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      {open && !loading && data !== null && (
        <div className="p-0">
          {children(data)}
        </div>
      )}
    </div>
  )
}

function SnapshotEntry({ snapshot, onRollback, isRollingBack, isSelected, onSelect }) {
  const [expanded, setExpanded] = useState(false)
  const shortHash = snapshot.snapshotHash?.slice(0, 8) ?? '????????'

  const handleExpand = () => {
    setExpanded((v) => !v)
    onSelect()
  }

  return (
    <div
      className={`relative border rounded-lg transition-all duration-150 ${
        isSelected
          ? 'border-accent bg-accent-faint/30'
          : 'border-border bg-surface hover:border-border-bright'
      } animate-fade-in`}
    >
      {/* Hash watermark */}
      <span className="hash-watermark opacity-40 text-4xl" aria-hidden="true">
        {shortHash}
      </span>

      <div className="relative p-4">
        <button
          className="w-full text-left"
          onClick={handleExpand}
          aria-expanded={expanded}
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-accent text-sm font-semibold tracking-wider flex-shrink-0">
                {shortHash}
              </span>
              {snapshot.message && (
                <span className="text-text-dim text-xs truncate">
                  {snapshot.message}
                </span>
              )}
            </div>
            <StatusBadge status={snapshot.status} size="xs" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-2xs text-text-faint font-mono tracking-widest uppercase mb-0.5">
                Tokens
              </p>
              <p className="text-xs font-mono text-text">
                {(snapshot.tokenUsage ?? 0).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-2xs text-text-faint font-mono tracking-widest uppercase mb-0.5">
                Latency
              </p>
              <p className="text-xs font-mono text-text">
                {snapshot.latencyMs != null ? `${snapshot.latencyMs}ms` : '—'}
              </p>
            </div>
            <div>
              <p className="text-2xs text-text-faint font-mono tracking-widest uppercase mb-0.5">
                Created
              </p>
              <p className="text-xs font-mono text-text-dim">
                {formatTime(snapshot.createdAt)}
              </p>
            </div>
          </div>
        </button>

        {expanded && (
          <div className="mt-4 flex flex-col gap-2 border-t border-border pt-4">
            <JsonViewer data={snapshot.agentYaml} label="agent.yaml" />
            <JsonViewer data={snapshot.memoryState} label="memory_state" />

            <LazySection
              label="files"
              loader={() => listSnapshotFiles(snapshot.workspaceId, snapshot.id)}
            >
              {(files) => (
                <FileExplorer
                  files={files}
                  workspaceId={snapshot.workspaceId}
                  snapshotId={snapshot.id}
                />
              )}
            </LazySection>

            {snapshot.parentSnapshotId && (
              <LazySection
                label="diff vs parent"
                loader={() => getSnapshotDiff(snapshot.workspaceId, snapshot.id)}
              >
                {(diffs) => <DiffViewer diffs={diffs} />}
              </LazySection>
            )}

            <div className="flex justify-end mt-2">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onRollback(snapshot.id)
                }}
                disabled={isRollingBack || snapshot.status === 'ROLLED_BACK'}
                className="px-3 py-1.5 text-xs font-mono font-medium border border-yellow text-yellow rounded hover:bg-yellow-faint transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label={`Roll back to snapshot ${shortHash}`}
              >
                {isRollingBack ? 'Rolling back…' : 'Roll back to this'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function SnapshotTimeline({ snapshots, loading, error, onRollback, rollingBack, onSelectSnapshot }) {
  const [selectedId, setSelectedId] = useState(null)

  const handleSelect = (snapshot) => {
    const next = selectedId === snapshot.id ? null : snapshot.id
    setSelectedId(next)
    if (onSelectSnapshot) onSelectSnapshot(next ? snapshot : null)
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-lg bg-surface border border-border animate-pulse" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red bg-red-faint px-4 py-3">
        <p className="text-sm text-red">Failed to load snapshots: {error}</p>
      </div>
    )
  }

  if (snapshots.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 border border-border rounded-lg bg-surface">
        <p className="text-text-dim text-sm mb-1">No snapshots yet</p>
        <p className="text-text-faint text-xs font-mono">
          Waiting for the agent to record its first state
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2" role="list" aria-label="Snapshot timeline">
      {snapshots.map((snapshot) => (
        <div key={snapshot.id} role="listitem">
          <SnapshotEntry
            snapshot={snapshot}
            onRollback={onRollback}
            isRollingBack={rollingBack === snapshot.id}
            isSelected={selectedId === snapshot.id}
            onSelect={() => handleSelect(snapshot)}
          />
        </div>
      ))}
    </div>
  )
}
